import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import {
  DateRange,
  FiltersDefinition,
  OneAlert,
  OneDataCollection,
} from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  Download,
  LayersFront,
  Pencil,
  Replace,
} from '@factorialco/f0-react/icons/app'
import { Warning as WarningIcon } from '@factorialco/f0-react/icons/app'
import {
  type AvailableCurrencyFragment,
  FinanceValueObjectsFinancialDocumentOperationTypeEnum,
  FinanceValueObjectsFinancialDocumentTypeEnum,
  SaleInvoiceFiltersProjectsDocument,
  SaleInvoiceFiltersVendorsDocument,
  SaleInvoicesListTableDocument,
  type SaleInvoicesListTableQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { issuedWithErrors } from 'modules/finance/components/Invoices/SaleInvoiceActivity/utils'
import { useDuplicateSaleInvoice } from 'modules/finance/hooks/sale_invoices/useDuplicateSaleInvoice'
import {
  mapDocumentTypeText,
  mapSaleInvoiceStatusText,
  saleInvoiceStatusTagType,
} from 'modules/finance/lib/invoices'
import { UpsellSalesInvoiceFeatureModal } from 'modules/project_management/components/upsells/UpsellSalesInvoiceFeatureModal'
import { bannerVariants } from 'modules/project_management/lib/getBannerVariants'

type SaleInvoiceRow =
  SaleInvoicesListTableQuery['finance']['financialDocumentsConnection']['nodes'][number]

export interface SaleInvoicesListTableProps {
  companyId: number
  locale: string
  legalEntitiesOptions: { value: number; label: string }[]
  availableCurrencies: AvailableCurrencyFragment[]
  showDeleteModalForInvoiceId: (invoiceId: number) => void
  projectId?: number
  dealId?: number
  clientId?: number
  onClickAdd?: () => void
  detailPath: (invoiceId: number) => string
  onClickExport?: () => void
  createInvoicesFeatureAvailable: boolean
  usage: number
  usageLimit: number
  projectsOrigin?: boolean
  currentEmployeeId: number
  pageDetailPath?: (invoiceId: number) => string
}

/**
 * Drop-in ODC-based replacement for SaleInvoicesDataTable.
 */
export const SaleInvoicesListTable = ({
  companyId,
  locale,
  legalEntitiesOptions,
  availableCurrencies,
  showDeleteModalForInvoiceId,
  projectId,
  dealId,
  clientId,
  onClickAdd,
  detailPath,
  onClickExport,
  createInvoicesFeatureAvailable,
  usage,
  usageLimit,
  currentEmployeeId,
  projectsOrigin = false,
  pageDetailPath,
}: SaleInvoicesListTableProps) => {
  const { handleDuplicateSaleInvoice } = useDuplicateSaleInvoice()
  const client = useApolloClient()

  const navigate = useNavigate()

  const { navigation } = useNavigation()
  const [upsellRequested, setUpsellRequested] = useState(false)
  const bannerProps = bannerVariants(usage, usageLimit)

  const handleOnClickAdd = () => {
    track(trackingEvents.accounting.saleInvoices.startCreate)
    createInvoicesFeatureAvailable ? onClickAdd?.() : setUpsellRequested(true)
  }

  const handleOnClickInfo = () => {
    track(trackingEvents.projectManagement.salesInvoices.banner.clickRequestInformation, {
      usage: `${usage}/${usageLimit}`,
    })
    setUpsellRequested(true)
  }

  const handleOnCloseUpsellModal = () => setUpsellRequested(false)

  const showBanner = usageLimit > 0

  const filtersDefinition = () => {
    const filters = {
      documentDate: {
        type: 'date',
        label: i18n.t('finance.sale_invoices.datatable.fields.created'),
        options: { mode: 'range', view: 'day' },
      },
      dueDate: {
        type: 'date',
        label: i18n.t('finance.sale_invoices.datatable.fields.due'),
        options: { mode: 'range', view: 'day' },
      },
      status: {
        type: 'in',
        label: i18n.t('finance.sale_invoices.datatable.fields.status'),
        options: {
          options: [
            { label: i18n.t('finance.sale_invoices.status.draft'), value: false },
            { label: i18n.t('finance.sale_invoices.status.issued'), value: true },
          ],
        },
      },
      documentType: {
        type: 'in',
        label: i18n.t('finance.invoices.list.fields.document_type'),
        options: {
          options: [
            { label: i18n.t('finance.invoices.detail.document_type.invoice'), value: 'invoice' },
            {
              label: i18n.t('finance.invoices.detail.document_type.credit_note'),
              value: 'credit_note',
            },
          ],
        },
      },
      currency: {
        type: 'in',
        label: i18n.t('finance.invoices.list.fields.currency'),
        options: {
          cache: true,
          options: async () =>
            availableCurrencies
              .filter((coreCurrency: AvailableCurrencyFragment) => {
                return !!coreCurrency.symbol
              })
              .map((coreCurrency: AvailableCurrencyFragment) => {
                return {
                  label: `${coreCurrency.name} (${coreCurrency.isoCode} - ${coreCurrency.symbol})`,
                  value: coreCurrency.isoCode,
                }
              }),
        },
      },
      legalEntity: {
        type: 'in',
        label: i18n.t('finance.sale_invoices.datatable.fields.legal_entity'),
        options: {
          cache: true,
          options: async () => legalEntitiesOptions,
        },
      },
      issuedWithErrors: {
        type: 'in',
        label: i18n.t('finance.sale_invoices.datatable.fields.errors'),
        options: {
          options: [
            { label: i18n.t('finance.sale_invoices.datatable.with_errors'), value: true },
            { label: i18n.t('finance.sale_invoices.datatable.without_errors'), value: false },
          ],
        },
      },
      ...(!projectId && {
        projectIds: {
          type: 'in',
          label: i18n.t('finance.invoices.list.fields.project'),
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: SaleInvoiceFiltersProjectsDocument,
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.projectManagement?.financialProjectsConnection?.nodes?.map(
                  (project) => ({
                    label: project.code ? `${project.code} - ${project.name}` : project.name,
                    value: project.id.toString(),
                  })
                ) ?? []
              )
            },
          },
        },
      }),
      ...(!clientId && {
        vendorIds: {
          type: 'in',
          label: i18n.t('finance.sale_invoices.datatable.fields.customer'),
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: SaleInvoiceFiltersVendorsDocument,
                variables: { companyId },
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.finance?.contactsConnection?.nodes?.map((contact) => ({
                  label:
                    contact.name +
                    (contact.name !== contact.legalName ? ` (${contact.legalName})` : ''),
                  value: contact.id.toString(),
                })) ?? []
              )
            },
          },
        },
      }),
    } satisfies FiltersDefinition

    return filters
  }

  const source = useGraphqlDataCollectionSource({
    filters: filtersDefinition(),
    document: SaleInvoicesListTableDocument,
    search: { enabled: true },
    primaryActions: () =>
      onClickAdd
        ? {
            label: i18n.t('finance.sale_invoices.datatable.create_invoice'),
            icon: Add,
            onClick: handleOnClickAdd,
          }
        : undefined,
    secondaryActions: () => [
      {
        enabled: !!onClickExport,
        label: i18n.t('finance.sale_invoices.datatable.export'),
        icon: Download,
        onClick: () => onClickExport?.(),
      },
    ],
    mapVariables: ({ search, filters }) => {
      const formatDateFilter = (dateOrRange?: Date | DateRange) => {
        if (!dateOrRange) return undefined
        if (dateOrRange instanceof Date) {
          return { eq: dateOrRange.toISOString() }
        }
        return { between: [dateOrRange.from.toISOString(), dateOrRange.to?.toISOString()] }
      }

      const gqlFilter: Record<string, unknown> = {
        ...(filters.documentDate ? { documentDate: formatDateFilter(filters.documentDate) } : {}),
        ...(filters.dueDate ? { dueDate: formatDateFilter(filters.dueDate) } : {}),
        ...(Array.isArray(filters.legalEntity) && filters.legalEntity.length > 0
          ? { legalEntityId: { in: filters.legalEntity } }
          : {}),
        ...(Array.isArray(filters.currency) && filters.currency.length > 0
          ? { currency: { in: filters.currency } }
          : {}),
        ...(Array.isArray(filters.status) && filters.status.length === 1
          ? { validated: { eq: filters.status[0] } }
          : {}),
        ...(Array.isArray(filters.issuedWithErrors) && filters.issuedWithErrors.length === 1
          ? { issuedWithErrors: { eq: filters.issuedWithErrors[0] } }
          : {}),
        ...(Array.isArray(filters.projectIds) && filters.projectIds.length > 0
          ? { projectId: { in: filters.projectIds.map((id) => Number(id)) } }
          : {}),
        ...(Array.isArray(filters.vendorIds) && filters.vendorIds.length > 0
          ? { vendorId: { in: filters.vendorIds.map((id) => Number(id)) } }
          : {}),
      }

      function isDocumentType(
        value: string
      ): value is FinanceValueObjectsFinancialDocumentTypeEnum {
        return value === 'invoice' || value === 'credit_note' || value === 'receipt'
      }

      const documentTypes: FinanceValueObjectsFinancialDocumentTypeEnum[] | undefined =
        Array.isArray(filters.documentType)
          ? filters.documentType.filter(isDocumentType)
          : undefined

      const operationTypes: FinanceValueObjectsFinancialDocumentOperationTypeEnum[] = ['sale']

      return {
        companyId,
        search,
        projectId,
        dealId,
        vendorId: clientId,
        documentTypes,
        operationTypes,
        filter: Object.keys(gqlFilter).length ? gqlFilter : null,
      }
    },
    mapData: (data) => data.finance?.financialDocumentsConnection?.nodes ?? [],
    mapPagination: (data) => ({
      total: data.finance?.financialDocumentsConnection?.totalCount ?? 0,
    }),
    totalItemSummary: (totals) => {
      return i18n.tp('finance.sale_invoices.datatable.total_items', { count: totals })
    },

    itemUrl: ({ id }) => (pageDetailPath ? pageDetailPath(Number(id)) : detailPath(Number(id))),
    itemActions: (item) =>
      [
        {
          label: i18n.t('finance.sale_invoices.datatable.actions.create_credit_note'),
          icon: Replace,
          enabled: !!item.validatedAt && !item.childFinancialDocument,
          onClick: () => {
            navigate(
              navigation.finance.sales.salesInvoices.invoiceId.fullPath({
                invoiceId: Number(item.id),
              }) + '?createCreditNote=true'
            )
          },
        },
        {
          label: i18n.t('finance.sale_invoices.datatable.actions.see_credit_note'),
          icon: Replace,
          enabled: !!item.childFinancialDocument,
          onClick: () => {
            if (item.childFinancialDocument?.id) {
              navigate(
                pageDetailPath
                  ? pageDetailPath(Number(item.childFinancialDocument.id))
                  : detailPath(Number(item.childFinancialDocument.id))
              )
            }
          },
        },
        {
          label: i18n.t('finance.sale_invoices.datatable.actions.duplicate'),
          icon: LayersFront,
          enabled: item.documentType === 'invoice',
          onClick: () => {
            handleDuplicateSaleInvoice({
              invoiceId: Number(item.id),
              currentEmployeeId: currentEmployeeId,
            }).then((id) => {
              if (id) {
                navigate(
                  navigation.finance.sales.salesInvoices.invoiceId.fullPath({
                    invoiceId: Number(id),
                  })
                )
              }
            })
          },
        },
        {
          label: i18n.t('finance.sale_invoices.datatable.actions.edit_draft'),
          icon: Pencil,
          enabled: item.validatedAt === null && item.registerSaleAt === null,
          onClick: () => navigate(detailPath(Number(item.id))),
        },
        {
          label: i18n.t('finance.sale_invoices.datatable.actions.delete_draft_invoice'),
          icon: Delete,
          critical: true,
          enabled: item.validatedAt === null && item.registerSaleAt === null,
          onClick: () => showDeleteModalForInvoiceId(Number(item.id)),
        },
      ].filter(Boolean),
  })

  return (
    <>
      {Boolean(showBanner) && (
        <Box paddingBottom="s20">
          <OneAlert
            title={bannerProps.title}
            description={bannerProps.description}
            variant={bannerProps.variant}
            action={{
              label: i18n.t('project_management.sale_invoices.banners.button'),
              onClick: handleOnClickInfo,
            }}
            {...(projectsOrigin && {
              link: {
                label: i18n.t('project_management.sale_invoices.banners.button_two'),
                href: navigation.finance.sales.salesInvoices.fullPath(),
              },
            })}
          />
        </Box>
      )}

      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.invoice'),
                  render: (item) =>
                    item.documentNumber || (item.salesInvoiceSequence?.humanReadablePreview ?? '-'),
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.status'),
                  render: (item) => ({
                    type: 'status',
                    value: {
                      status: saleInvoiceStatusTagType(!!item.validatedAt, !!item.registerSaleAt),
                      label: mapSaleInvoiceStatusText(!!item.validatedAt, !!item.registerSaleAt),
                    },
                  }),
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.errors'),
                  render: (item) => {
                    if (issuedWithErrors(!!item.validatedAt, item.financialDocumentEvents)) {
                      return {
                        type: 'alertTag' as const,
                        value: {
                          label: i18n.t('finance.sale_invoices.datatable.with_errors'),
                          icon: WarningIcon,
                          level: 'critical',
                        },
                      }
                    }

                    return { type: 'text', value: '' }
                  },
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.credit_note'),
                  render: (item: SaleInvoiceRow) => ({
                    type: 'text' as const,
                    value: {
                      text:
                        item.childFinancialDocument?.documentNumber ||
                        item.childFinancialDocument?.salesInvoiceSequence?.humanReadablePreview ||
                        '—',
                    },
                  }),
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.document_type'),
                  render: (item) =>
                    item.documentType ? mapDocumentTypeText(item.documentType) : '—',
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.amount'),
                  align: 'right' as const,
                  render: (item) =>
                    item.currency
                      ? formattedAmount({
                          amountCents: Number(item.totalAmountCents),
                          currency: item.currency,
                          locale,
                        })
                      : '-',
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.customer'),
                  render: (item) =>
                    item.vendor?.name !== undefined
                      ? item.vendor.name +
                        (item.vendor.legalName && item.vendor.legalName !== item.vendor.name
                          ? ` (${item.vendor.legalName})`
                          : '')
                      : '—',
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.due'),
                  render: (item) =>
                    item.dueDate
                      ? convertDate(item.dueDate, 'backend_datetime', 'ui_dmy_long')
                      : '—',
                },
                {
                  label: i18n.t('finance.sale_invoices.datatable.fields.operation_date'),
                  render: (item) =>
                    item.documentDate
                      ? convertDate(item.documentDate, 'backend_datetime', 'ui_dmy_long')
                      : '—',
                },
              ],
            },
          },
        ]}
      />

      {upsellRequested && <UpsellSalesInvoiceFeatureModal onClose={handleOnCloseUpsellModal} />}
    </>
  )
}
